/* =============================================================================
   SLOAT product page
   - VariantForm (from sloat-core.js) drives options, price and the button.
   - Gallery follows the chosen color with a short crossfade; URL keeps ?variant=.
   - Phone slider progress bar.
   - Zoom: full-screen stack of the visible images, opens at the tapped one.
   - Sticky add to cart on phones once the main button leaves the screen.
   ========================================================================== */
const S = window.Sloat;
const { $, $$ } = S;
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.classList.contains('sloat-motion-reduced');

class ProductPage {
  constructor(root) {
    this.root = root;
    this.form = $('form[data-product-form]', root);
    this.track = $('[data-gallery-track]', root);
    this.bar = $('[data-gallery-bar]', root);
    this.sticky = $('[data-sticky-atc]', root);
    this.colorIndex = this.findColorIndex();
    this.activeColor = null;

    this.vf = new S.VariantForm(this.form, { onChange: (d) => this.onVariant(d) });
    this.initSlider();
    this.initZoom();
    this.initSticky();
    this.updateSticky();
    $('[data-dynamic-guard]', root)?.addEventListener('click', () => this.vf.validate());
  }

  findColorIndex() {
    const fs = $$('[data-option-index]', this.form).find((f) => /colou?r/i.test($('.sloat-label', f)?.textContent || ''));
    return fs ? Number(fs.dataset.optionIndex) : -1;
  }

  onVariant({ variant, selected, initial }) {
    // URL
    if (variant && !initial) {
      const u = new URL(location.href);
      u.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', u.pathname + u.search);
    }
    // Price roll
    if (!initial) {
      $$('[data-price]', this.root).forEach((el) => {
        el.classList.remove('is-changing');
        void el.offsetWidth;
        el.classList.add('is-changing');
      });
    }
    // Gallery by color
    const color = this.colorIndex >= 0 ? selected[this.colorIndex] : null;
    if (color && color !== this.activeColor) {
      const first = this.activeColor === null;
      this.activeColor = color;
      if (!first || initial) this.showColor(color, !first);
    }
    if (variant && variant.media && !this.groupsActive()) this.scrollToMedia(variant.media);
    this.updateSticky();
  }

  groupsActive() {
    return $$('[data-media-color]', this.track).some((li) => li.dataset.mediaColor);
  }

  showColor(color, animate) {
    if (!this.track || !this.groupsActive()) return;
    const apply = () => {
      $$('[data-media-color]', this.track).forEach((li) => {
        const c = li.dataset.mediaColor;
        li.hidden = !!c && c !== color;
      });
      $$('[data-media-color]:not([hidden]) img[loading="lazy"]', this.track)
        .slice(0, 2)
        .forEach((img) => (img.loading = 'eager'));
      this.track.scrollTo({ left: 0 });
      this.updateBar();
    };
    if (!animate || reduced()) return apply();
    this.track.classList.add('is-swapping');
    setTimeout(() => {
      apply();
      requestAnimationFrame(() => this.track.classList.remove('is-swapping'));
    }, 320);
  }

  scrollToMedia(id) {
    const li = $(`[data-media-id="${id}"]`, this.track);
    if (!li || li.hidden) return;
    if (matchMedia('(max-width: 749px)').matches) this.track.scrollTo({ left: li.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft || 0), behavior: reduced() ? 'auto' : 'smooth' });
  }

  visibleMedia() {
    return $$('.sloat-pdp__media:not([hidden])', this.track);
  }

  initSlider() {
    if (!this.track || !this.bar) return;
    let raf;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.updateBar());
      },
      { passive: true }
    );
    this.updateBar();
  }

  updateBar() {
    if (!this.bar) return;
    const items = this.visibleMedia();
    const n = Math.max(1, items.length);
    const max = this.track.scrollWidth - this.track.clientWidth;
    const progress = max > 0 ? this.track.scrollLeft / max : 0;
    this.bar.style.setProperty('--w', `${100 / n}%`);
    this.bar.style.setProperty('--p', String(progress * (n - 1)));
  }

  initZoom() {
    const dialog = $('[data-sloat-dialog^="zoom-"]', this.root);
    if (!dialog) return;
    const list = $('[data-zoom-list]', dialog);
    this.root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-zoom]');
      if (!btn || matchMedia('(max-width: 749px)').matches) return;
      const items = this.visibleMedia();
      const index = items.indexOf(btn.closest('.sloat-pdp__media'));
      list.innerHTML = items
        .map((li) => {
          const img = $('img', li);
          if (!img) return '';
          const src = img.currentSrc || img.src;
          const big = src.replace(/([?&])width=\d+/, '$1width=2400');
          return `<li><img src="${big}" alt="${(img.alt || '').replace(/"/g, '&quot;')}" loading="lazy" data-sloat-close></li>`;
        })
        .join('');
      const rect = btn.getBoundingClientRect();
      dialog.style.setProperty('--zoom-origin', `${Math.round(((rect.top + rect.height / 2) / innerHeight) * 100)}%`);
      S.dialogs.open(dialog, { trigger: btn }).then(() => {
        const target = list.children[Math.max(0, index)];
        const panel = $('[data-panel]', dialog);
        if (target && panel) panel.scrollTop = target.offsetTop - 8;
        const tImg = $('img', target);
        if (tImg) tImg.loading = 'eager';
      });
    });
  }

  initSticky() {
    if (!this.sticky) return;
    const buy = $('[data-buy]', this.root);
    const footer = document.querySelector('.sloat-footer');
    let buyVisible = true;
    let footerVisible = false;
    const toggle = () => {
      const show = !buyVisible && !footerVisible;
      this.sticky.classList.toggle('is-visible', show);
      this.sticky.setAttribute('aria-hidden', String(!show));
      $('[data-sticky-add]', this.sticky).tabIndex = show ? 0 : -1;
    };
    // Scroll-driven (IntersectionObserver misses jumps past the button).
    let raf;
    const check = () => {
      raf = null;
      const b = buy && buy.getBoundingClientRect();
      buyVisible = !b || b.bottom > 0;
      footerVisible = !!footer && footer.getBoundingClientRect().top < innerHeight;
      toggle();
    };
    window.addEventListener('scroll', () => raf || (raf = requestAnimationFrame(check)), { passive: true });
    window.addEventListener('resize', () => raf || (raf = requestAnimationFrame(check)), { passive: true });
    check();

    $('[data-sticky-add]', this.sticky).addEventListener('click', () => {
      if (!this.vf.complete) {
        this.vf.validate();
        return;
      }
      this.form.requestSubmit ? this.form.requestSubmit($('[data-add-button]', this.form)) : $('[data-add-button]', this.form).click();
    });
  }

  updateSticky() {
    if (!this.sticky || !this.vf) return;
    const summary = $('[data-sticky-summary]', this.sticky);
    const button = $('[data-sticky-add]', this.sticky);
    const sel = this.vf.selected().filter(Boolean);
    const price = this.vf.variant ? S.money(this.vf.variant.price) : $('[data-price]', this.root)?.textContent || '';
    if (summary) summary.textContent = [...sel, price].join(' · ');
    if (button) {
      const soldOut = this.vf.variant && !this.vf.variant.available;
      button.textContent = soldOut ? 'Sold out' : this.vf.complete ? 'Add to cart' : 'Select size';
      button.disabled = !!soldOut;
    }
  }
}

const init = () => $$('[data-product-root]').forEach((el) => !el._pdp && (el._pdp = new ProductPage(el)));
if (S && S.ready) init();
else document.addEventListener('sloat:ready', init, { once: true });
document.addEventListener('shopify:section:load', init);
